import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import LetterheadToggle, { useLetterheadMode } from "@/components/LetterheadToggle";

export default function PrintDischarge() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const letterhead = useLetterheadMode();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/ipd/discharge/${id}`);
      setD(data);
      const me = await api.get("/auth/me");
      setHospital(me.data.hospital);
      setBranch(me.data.branch);
      const auto = new URLSearchParams(window.location.search).get("autoprint");
      if (auto !== "0") setTimeout(() => window.print(), 600);
    })();
  }, [id]);

  if (!d) return <div className="p-10 text-sm text-ink-muted">Loading…</div>;

  const rows = [
    ["Final diagnosis", d.final_diagnosis],
    ["Procedure performed", d.procedure_performed],
    ["Clinical summary", d.clinical_summary],
    ["Investigation summary", d.investigation_summary],
    ["Treatment given", d.treatment_given],
    ["Operative details", d.operative_details],
    ["Condition at discharge", d.condition_at_discharge],
  ];
  const rxRows = Array.isArray(d.discharge_prescription) ? d.discharge_prescription.filter(m => (m.medicine || "").trim()) : [];
  const afterRx = [
    ["Follow-up advice", d.follow_up_advice],
    ["Emergency instructions", d.emergency_instructions],
    ["Red-flag symptoms", d.red_flag_symptoms],
    ["Diet / activity", d.diet_activity],
    ["Post-op eye care", d.post_op_eye_care],
  ];

  return (
    <div className="mx-auto max-w-3xl bg-white p-10 text-sm text-ink">
      <LetterheadToggle />
      {!letterhead && (
        <PrintHeader
          hospital={hospital}
          branch={branch}
          subtitle="Discharge Summary · NABH-compliant"
          right={<>
            <div>Admitted: {(d.admitted_at||"").slice(0,10)}</div>
            <div>Discharged: {(d.discharged_at||"").slice(0,10)}</div>
          </>}
        />
      )}

      <div className="mt-4 grid grid-cols-3 gap-3 border-b border-border pb-3 text-xs">
        <div><div className="font-semibold uppercase text-ink-muted">Patient</div><div className="text-sm">{d.patient_name}</div></div>
        <div><div className="font-semibold uppercase text-ink-muted">UHID</div><div className="text-sm font-mono">{d.patient_uhid}</div></div>
        <div><div className="font-semibold uppercase text-ink-muted">Doctor</div><div className="text-sm">{d.doctor_name}</div></div>
        {d.ipd_no && <div><div className="font-semibold uppercase text-ink-muted">IPD No.</div><div className="text-sm font-mono">{d.ipd_no}</div></div>}
        {(d.ward || d.bed_no) && <div><div className="font-semibold uppercase text-ink-muted">Ward / Bed</div><div className="text-sm">{d.ward || "—"}{d.bed_no ? ` · ${d.bed_no}` : ""}</div></div>}
        {d.admitted_at && <div><div className="font-semibold uppercase text-ink-muted">Admission date</div><div className="text-sm">{d.admitted_at.slice(0, 16).replace("T", " ")}</div></div>}
        {d.discharged_at && <div><div className="font-semibold uppercase text-ink-muted">Discharge date</div><div className="text-sm">{d.discharged_at.slice(0, 16).replace("T", " ")}</div></div>}
      </div>

      {/* Panel / Insurance block — snapshot from admission */}
      {(d.payer_category && d.payer_category !== "cash" && (d.panel_name || d.policy_no)) && (
        <div className="mt-3 rounded-md border border-brand/30 bg-brand/5 p-2 text-[11px]" data-testid="print-discharge-panel-block">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-brand">Panel / Insurance</div>
          <div className="grid grid-cols-4 gap-2">
            <div><div className="text-[9px] uppercase text-ink-muted">Payer category</div><div className="capitalize">{d.payer_category}</div></div>
            {d.panel_name && <div><div className="text-[9px] uppercase text-ink-muted">Panel / Company</div><div>{d.panel_name}</div></div>}
            {d.policy_no && <div><div className="text-[9px] uppercase text-ink-muted">Policy / Card no.</div><div className="font-mono">{d.policy_no}</div></div>}
            {d.tpa_name && <div><div className="text-[9px] uppercase text-ink-muted">TPA</div><div>{d.tpa_name}</div></div>}
            {d.corporate_name && <div><div className="text-[9px] uppercase text-ink-muted">Corporate</div><div>{d.corporate_name}</div></div>}
          </div>
        </div>
      )}

      <section className="mt-4 space-y-3">
        {rows.map(([k, v]) => v ? (
          <div key={k}><div className="font-semibold uppercase text-[10px] text-brand">{k}</div><div className="whitespace-pre-line">{v}</div></div>
        ) : null)}

        {/* Discharge medication — structured Rx table (fallback to text if legacy) */}
        {rxRows.length > 0 ? (
          <div>
            <div className="font-semibold uppercase text-[10px] text-brand">Discharge medication</div>
            <table className="mt-1 w-full border-collapse text-[11px]">
              <thead>
                <tr className="border-b border-ink/40 text-left text-[9px] uppercase tracking-wider text-ink-muted">
                  <th className="py-1 pr-2">#</th>
                  <th className="py-1 pr-2">Medicine</th>
                  <th className="py-1 pr-2">Strength</th>
                  <th className="py-1 pr-2">Dose</th>
                  <th className="py-1 pr-2">Route</th>
                  <th className="py-1 pr-2">Eye</th>
                  <th className="py-1 pr-2">Frequency</th>
                  <th className="py-1 pr-2">Timing</th>
                  <th className="py-1 pr-2">Duration</th>
                  <th className="py-1">Instructions</th>
                </tr>
              </thead>
              <tbody>
                {rxRows.map((m, i) => (
                  <tr key={i} className="border-b border-border align-top">
                    <td className="py-1 pr-2 text-ink-muted">{i + 1}</td>
                    <td className="py-1 pr-2 font-medium">{m.medicine}</td>
                    <td className="py-1 pr-2">{m.strength || "—"}</td>
                    <td className="py-1 pr-2">{m.dosage || "—"}</td>
                    <td className="py-1 pr-2">{m.route || "—"}</td>
                    <td className="py-1 pr-2">{m.eye || "—"}</td>
                    <td className="py-1 pr-2">{m.frequency || "—"}</td>
                    <td className="py-1 pr-2">{m.timing || "—"}</td>
                    <td className="py-1 pr-2">{m.duration || "—"}</td>
                    <td className="py-1">{m.instructions || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : d.discharge_medication ? (
          <div><div className="font-semibold uppercase text-[10px] text-brand">Discharge medication</div><div className="whitespace-pre-line">{d.discharge_medication}</div></div>
        ) : null}

        {afterRx.map(([k, v]) => v ? (
          <div key={k}><div className="font-semibold uppercase text-[10px] text-brand">{k}</div><div className="whitespace-pre-line">{v}</div></div>
        ) : null)}

        {/* Selected nursing notes — only those the nurse/doctor ticked for inclusion */}
        {Array.isArray(d.nursing_notes_for_print) && d.nursing_notes_for_print.length > 0 && (
          <div data-testid="print-discharge-nursing-block">
            <div className="font-semibold uppercase text-[10px] text-brand">Nursing notes</div>
            <table className="mt-1 w-full border-collapse text-[10.5px]">
              <thead>
                <tr className="border-b border-ink/40 text-left text-[9px] uppercase tracking-wider text-ink-muted">
                  <th className="py-1 pr-2">When</th>
                  <th className="py-1 pr-2">By</th>
                  <th className="py-1 pr-2">BP</th>
                  <th className="py-1 pr-2">Pulse</th>
                  <th className="py-1 pr-2">Temp</th>
                  <th className="py-1 pr-2">SpO2</th>
                  <th className="py-1 pr-2">Medications</th>
                  <th className="py-1">Notes</th>
                </tr>
              </thead>
              <tbody>
                {d.nursing_notes_for_print.map((n) => {
                  const meds = Array.isArray(n.medications) && n.medications.length > 0
                    ? n.medications.map((m) => [m.medicine, m.strength, m.dose, m.route, m.time].filter(Boolean).join(" · ")).filter(Boolean).join("; ")
                    : (n.medications_given || "");
                  return (
                    <tr key={n.id} className="border-b border-border align-top">
                      <td className="py-1 pr-2 whitespace-nowrap">{(n.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-1 pr-2">{n.created_by_name || "—"}</td>
                      <td className="py-1 pr-2">{n.bp || "—"}</td>
                      <td className="py-1 pr-2">{n.pulse || "—"}</td>
                      <td className="py-1 pr-2">{n.temperature || "—"}</td>
                      <td className="py-1 pr-2">{n.spo2 || "—"}</td>
                      <td className="py-1 pr-2">{meds || "—"}</td>
                      <td className="py-1">{n.notes || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* IOL / Implant block — auto-bound from the patient's OT note */}
      <section className="mt-5 rounded-md border border-border p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="font-semibold uppercase text-[10px] text-brand">IOL / Implant used</div>
          {d.iol_used && d.iol_used.implant_name && <div className="text-[10px] text-ink-muted">{d.iol_used.implant_name}</div>}
        </div>
        <div className="grid grid-cols-5 gap-3 text-[11px]">
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Model</div><div>{(d.iol_used && d.iol_used.model) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Power</div><div>{(d.iol_used && d.iol_used.power) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Batch</div><div className="font-mono">{(d.iol_used && d.iol_used.batch_no) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Lot</div><div className="font-mono">{(d.iol_used && d.iol_used.lot_no) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Expiry</div><div>{(d.iol_used && d.iol_used.expiry) || "—"}</div></div>
        </div>
        {/* Sticker paste box — physical IOL barcode/lot sticker is affixed here on the printed discharge */}
        <div className="mt-3 flex items-start gap-4">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-ink-muted">Paste IOL / implant sticker</div>
            <div className="mt-1 grid h-24 w-60 place-items-center rounded-md border-2 border-dashed border-ink/40 bg-secondary/30 text-[9px] uppercase tracking-widest text-ink-subtle">
              Affix sticker here
            </div>
          </div>
        </div>
      </section>

      <div className="mt-12 flex items-end justify-between">
        <div className="text-[11px] text-ink-muted">This summary forms part of the patient medical record (NABH).</div>
        <div className="border-t border-ink pt-1 text-right text-[11px]">{d.doctor_name}<br />{d.doctor_registration_no}</div>
      </div>
      <div className="mt-10 border-t border-border pt-3 text-center text-[10px] text-ink-muted">{BRAND.footer}</div>
    </div>
  );
}
